import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Handshake, X } from 'lucide-react';

import { Alert, AlertDescription } from '@/shared/components/ui/alert';
import { useAuth } from '@/shared/auth/AuthContext';
import { Button } from '@/shared/components/ui/button';

export type CollaborationModuleKey =
  | 'nexus'
  | 'accounting'
  | 'market_prices'
  | 'crm'
  | 'analytics'
  | 'partner_portal'
  | 'ai_assistant'
  | 'hr';

interface CollaborationBannerProps {
  moduleKey: CollaborationModuleKey;
  variant: 'existing' | 'new';
}

export function CollaborationBanner({ moduleKey, variant }: CollaborationBannerProps): JSX.Element | null {
  const { t } = useTranslation();
  const { user } = useAuth();
  const storageKey = `vanagreen_collab_banner_${moduleKey}_${user?.tenantId ?? 'none'}_v1`;

  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(storageKey) === '1';
    } catch {
      return false;
    }
  });

  if (dismissed) return null;

  const dismiss = (): void => {
    try {
      localStorage.setItem(storageKey, '1');
    } catch {
      // localStorage unavailable — still dismiss for this session
    }
    setDismissed(true);
  };

  return (
    <Alert variant="info" aria-label={t('collaborationBanner.ariaLabel')} data-testid={`collaboration-banner-${moduleKey}`}>
      <Handshake className="size-4" />
      <AlertDescription className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <p>{t(`collaborationBanner.${variant}.message`)}</p>
          <p className="flex flex-wrap items-center gap-x-4 gap-y-0.5 text-xs text-muted-foreground">
            <a
              href="https://wa.me/40743016746"
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-4 hover:text-foreground"
            >
              {t('help.contact.whatsapp')}: {t('help.contact.whatsappNumber')}
            </a>
            <a
              href="mailto:contact@vanadium.systems"
              className="underline underline-offset-4 hover:text-foreground"
            >
              {t('help.contact.email')}: {t('help.contact.emailAddress')}
            </a>
          </p>
        </div>
        <Button type="button" variant="ghost" size="icon" className="shrink-0" aria-label={t('collaborationBanner.dismiss')} onClick={dismiss}>
<X className="size-4" aria-hidden />
</Button>
      </AlertDescription>
    </Alert>
  );
}
